import { emptyMetrics, sumMetrics } from "./parse";
import { USAGE_TAG_NAMESPACES } from "./tags";
import type { TagNamespaceUsage, TagUsageRow, UsageMetrics } from "./types";

/**
 * Fold per-tag rows into per-namespace sections with honest coverage.
 *
 * Coverage is the load-bearing number on the page, because it is what tells a reader how
 * much of the bill a given dimension actually explains. Getting it subtly wrong is worse
 * than omitting it: a confidently-rendered 100% while half the spend is invisible is a
 * reassuring lie.
 */

/** `svc` is synthetic (it holds folded legacy tags), so it leads rather than being absent. */
const NAMESPACE_ORDER: readonly string[] = ["svc", ...USAGE_TAG_NAMESPACES];

export interface BuildNamespacesInput {
  rowsByNamespace: Map<string, TagUsageRow[]>;
  /** From `/team/daily/activity`. `null` when unavailable. */
  windowTotals: UsageMetrics | null;
  /** Namespaces where a cap was hit, so their rows are a subset. */
  truncatedNamespaces?: ReadonlySet<string>;
  /** Discovered-but-silent tag counts, keyed by namespace. */
  silentTagCounts?: ReadonlyMap<string, number>;
}

/**
 * Coverage above this is treated as aggregation lag rather than a bug worth surfacing.
 *
 * The tag and team aggregates are separate tables written by the same background writer, so
 * a window whose most recent day is still being flushed can legitimately show a numerator
 * slightly ahead of its denominator.
 */
const COVERAGE_LAG_TOLERANCE = 1.05;

export interface BuiltNamespaces {
  namespaces: TagNamespaceUsage[];
  warnings: string[];
}

export function buildNamespaces(input: BuildNamespacesInput): BuiltNamespaces {
  const truncated = input.truncatedNamespaces ?? new Set<string>();
  const silent = input.silentTagCounts ?? new Map<string, number>();
  const warnings: string[] = [];
  const namespaces: TagNamespaceUsage[] = [];

  const seen = new Set<string>();
  const ordered = [
    ...NAMESPACE_ORDER.filter((ns) => input.rowsByNamespace.has(ns)),
    // Anything the contract gained since this list was written still renders, just last.
    ...[...input.rowsByNamespace.keys()].filter(
      (ns) => !NAMESPACE_ORDER.includes(ns),
    ),
  ];

  for (const namespace of ordered) {
    if (seen.has(namespace)) continue;
    seen.add(namespace);

    const all = input.rowsByNamespace.get(namespace) ?? [];
    // A tag with no spend in the window is reported as a count, not rendered as a zero row —
    // a table of zeroes buries the rows that matter.
    const rows = all
      .filter((row) => row.metrics.spend > 0 || row.metrics.requests > 0)
      .sort((a, b) => b.metrics.spend - a.metrics.spend);

    const attributed = sumMetrics(rows.map((row) => row.metrics));
    const isTruncated = truncated.has(namespace);

    let coverage: number | null = null;
    let unattributedSpend: number | null = null;

    if (input.windowTotals && input.windowTotals.spend > 0 && !isTruncated) {
      coverage = attributed.spend / input.windowTotals.spend;
      unattributedSpend = Math.max(
        0,
        input.windowTotals.spend - attributed.spend,
      );
      if (coverage > COVERAGE_LAG_TOLERANCE) {
        warnings.push(`coverage-over-100:${namespace}`);
      }
    } else if (isTruncated) {
      // Deliberately null rather than a number: with an incomplete numerator, a coverage
      // figure would understate the dimension and look like missing spend.
      warnings.push(`truncated:${namespace}`);
    }

    namespaces.push({
      namespace,
      rows,
      attributed,
      unattributedSpend,
      coverage,
      silentTagCount: silent.get(namespace) ?? all.length - rows.length,
      truncated: isTruncated,
    });
  }

  return { namespaces, warnings };
}

/** An empty result, for the not-configured and unavailable paths. */
export function emptyNamespaces(): BuiltNamespaces {
  return { namespaces: [], warnings: [] };
}

export { emptyMetrics };
